import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type FormikHelpers } from 'formik'
import * as React from 'react'
import { Link } from 'react-router-dom'
import * as Api from 'src/api'
import { useAdminAccess } from 'src/helpers/hooks'
import { useStorageValue } from 'src/helpers/storage'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Section } from 'src/tailwind/components/Section'
import { Form } from 'src/views/components/forms/formik/Form'
import LocalFileInput from 'src/views/components/forms/formik/LocalFileInput'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import Swal from 'sweetalert2'
import MessageForm from '../../../lecturer/courses/scores/MessageForm'
import BulkMessageStatus from './BulkMessageStatus'
import SkippedRows from './SkippedRows'
import { bulkMessageBatchStorage, useBulkMessageProgress } from './useBulkMessageProgress'

interface BulkMessageValues {
  readonly file: File | null
  readonly subject: string
  readonly body: string
}

const INITIAL_VALUES: BulkMessageValues = { file: null, subject: '', body: '' }

// The server's limits
const FILE_EXTENSIONS = ['xlsx']
const FILE_MAX_SIZE = 1024 * 1024
const SUBJECT_MAX_LENGTH = 255
const BODY_MAX_LENGTH = 65000

/** The last send of this visit; its skipped rows are not kept across reloads. */
type SendOutcome =
  | { readonly kind: 'started'; readonly result: Api.AdministratorBulkMessage }
  | { readonly kind: 'rejected'; readonly error: Api.AdministratorBulkMessageRejected }

function isRejected(error: unknown): error is Api.AdministratorBulkMessageRejected {
  return (
    typeof error === 'object' && error != null && 'type' in error && error.type === 'AdministratorBulkMessageRejected'
  )
}

export default function AdministratorBulkMessagePage(): JSX.Element {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('bulk_message:bulk_message')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const headers = useAuthenticatedHeaders()
  const batchId = useStorageValue(bulkMessageBatchStorage)
  const progress = useBulkMessageProgress(batchId)
  const [outcome, setOutcome] = React.useState<SendOutcome | null>(null)
  const resultRef = React.useRef<HTMLDivElement>(null)
  // A stalled batch still blocks sending until it is closed, so a stuck job is not sent again by accident
  const isSending = progress?.status === 'loading' || progress?.status === 'sending' || progress?.status === 'stalled'

  React.useEffect(() => {
    if (outcome != null) {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [outcome])

  const handleSend = React.useCallback(
    async (values: BulkMessageValues, helpers: FormikHelpers<BulkMessageValues>) => {
      if (values.file == null || isSending) return

      const confirmation = await Swal.fire({
        title: t('bulk_message:confirm_send'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0D6EFD',
        cancelButtonColor: '#6C757D',
        confirmButtonText: t('message:send'),
        cancelButtonText: t('common:cancel'),
      })
      if (!confirmation.isConfirmed) return

      setOutcome(null)
      // Sending is disabled while a batch runs, so whatever is stored has finished or expired
      bulkMessageBatchStorage.setValue(null)
      try {
        const result = await Api.postAdministratorBulkMessage({
          headers,
          body: { file: values.file, subject: values.subject, body: values.body },
        })
        bulkMessageBatchStorage.setValue(result.batchId)
        setOutcome({ kind: 'started', result })
        helpers.resetForm()
      } catch (error) {
        // No row matched a recipient: nothing was sent, the form keeps its values for another file
        if (!isRejected(error)) throw error
        setOutcome({ kind: 'rejected', error })
      }
    },
    [headers, isSending, t]
  )

  const handleDismiss = React.useCallback(() => {
    bulkMessageBatchStorage.setValue(null)
    setOutcome(null)
  }, [])

  const summary = outcome?.kind === 'started' && outcome.result.batchId === batchId ? outcome.result : null
  const skippedRows = outcome?.kind === 'rejected' ? outcome.error.data.unmatched : (summary?.unmatched ?? [])

  return (
    <Section
      title={t('bulk_message:bulk_message')}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faPaperPlane} />}
      breadcrubms={[{ page: t('bulk_message:bulk_message'), path: `/${locale}/administrator/messages/bulk` }]}
    >
      <Instructions />
      {(progress != null || outcome != null) && (
        <div ref={resultRef} className="mb-6 flex flex-col gap-6">
          {progress != null && <BulkMessageStatus progress={progress} summary={summary} onDismiss={handleDismiss} />}
          {outcome?.kind === 'rejected' && (
            <div
              className="flex items-center gap-3 rounded-card bg-lightRed px-6 py-5 text-primaryRed"
              role="alert"
              data-testid="BulkMessageRejected"
            >
              <FontAwesomeIcon icon={ProRegularSvgIcons.faCircleExclamation} />
              <span>{outcome.error.message}</span>
            </div>
          )}
          {skippedRows.length > 0 && <SkippedRows rows={skippedRows} />}
        </div>
      )}
      <Form initialValues={INITIAL_VALUES} onSubmit={handleSend} isConfirmable>
        <MessageForm isSendDisabled={isSending} subjectMaxLength={SUBJECT_MAX_LENGTH} bodyMaxLength={BODY_MAX_LENGTH}>
          <LocalFileInput name="file" extensions={FILE_EXTENSIONS} maxSize={FILE_MAX_SIZE} required />
        </MessageForm>
      </Form>
    </Section>
  )
}

function Instructions(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const canViewMails = useAdminAccess().can('mails')

  return (
    <div className="mb-6 flex gap-3 rounded-card bg-card px-6 py-5 text-primaryTextColor">
      <FontAwesomeIcon icon={ProRegularSvgIcons.faCircleInfo} className="mt-1" />
      <div className="flex flex-col gap-2">
        <div>{t('bulk_message:file_instructions')}</div>
        <div>{t('bulk_message:background_sending')}</div>
        <div>
          {t('bulk_message:mail_statuses')}
          {canViewMails && (
            <>
              {' '}
              <Link
                to={`/${locale}/administrator/sensitive/mails`}
                className="text-primaryBlueLink hover:underline dark:text-primaryTextColor"
              >
                {t('common:mails')}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
